import React, {useState, memo} from 'react';
import {Button, Space, Switch, Typography} from 'antd';
import Editor from "@monaco-editor/react";
import DiffViewer from "@src/ui/DiffViewer";
import ResponseRenderer from "@src/ui/ResponseRenderer";
import GenUIRenderer from "@src/ui/GenUIRenderer";

export const tryParseToJSON = (value: any) => {
    let parsedData: any;
    if(value == null)
        parsedData = {}
    else if (typeof value === 'string')
        try {
            parsedData = JSON.parse(value);
        } catch {
            parsedData = value;
        }
    else
        parsedData = value;
    return parsedData;
}

export interface PackDataExpandedRowProps {
    initialValue?: any;
    compareValue?: any;
    isCompareMode?: boolean;
    dataPipeline?: any;
    dataComparePipeline?: any;
    isEditing: boolean;
    onStartEditing: () => void;
    onSubmitEdit: (editedJson: string) => void;
    onCancelEdit: () => void;
    genUIData?: any;
}

const PackDataExpandedRow = memo(({
    initialValue = {},
    compareValue = null,
    isCompareMode = false,
    dataPipeline,
    dataComparePipeline,
    isEditing,
    onStartEditing,
    onSubmitEdit,
    onCancelEdit,
    genUIData
}: PackDataExpandedRowProps) => {
    const [showGenUI, setShowGenUI] = useState<boolean>(true);
    const parsedData = tryParseToJSON(initialValue)

    let parsedCompareData: any = null;
    if(isCompareMode && compareValue != null)
        parsedCompareData = tryParseToJSON(compareValue);

    // Check if initialValue is empty
    const isEmptyData = !initialValue ||
        (typeof parsedData === 'object' &&
         !Array.isArray(parsedData) &&
         Object.keys(parsedData).length === 0);

    const [editedJson, setEditedJson] = useState<string>(
        typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData, null, 2)
    );

    const handleStartEditing = () => {
        setEditedJson(
            typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData, null, 2)
        );
        onStartEditing();
    };

    const handleSubmit = () => onSubmitEdit(editedJson);

    // Show "no data found" message for empty data (unless gen UI data exists)
    if (isEmptyData && !isCompareMode && !isEditing && !genUIData) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px', color: '#999' }}>
                No data found
            </div>
        );
    }

    const dataContent = (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', width: '100%', overflow: 'hidden' }}>
            {isCompareMode ? (
                <DiffViewer
                    oldValue={typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData || {}, null, 2)}
                    newValue={typeof parsedCompareData === 'string' ? parsedCompareData : JSON.stringify(parsedCompareData || {}, null, 2)}
                    leftTitle={`Pipeline${dataPipeline?.version ? ` ${dataPipeline.version}` : ''}`}
                    rightTitle={`Pipeline${dataComparePipeline?.version ? ` ${dataComparePipeline.version}` : ''}`}
                    compareMethod="lines"
                />
            ) : (
                <div style={{ display: 'flex', gap: '12px', width: '100%', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    {
                        isEditing ? (
                            <Editor
                                height="300px"
                                defaultLanguage={typeof parsedData === 'string' ? 'markdown' : 'json'}
                                value={editedJson}
                                onChange={(value) => setEditedJson(value || '')}
                                options={{ minimap: { enabled: false } }}
                            />
                        ) : (
                            <div style={{ width: '100%', overflow: 'hidden' }}>
                                <ResponseRenderer response={initialValue} />
                            </div>
                        )
                    }
                    </div>
                    <div style={{ flexShrink: 0 }}>
                        <Space orientation="vertical">
                        {
                            isEditing ? (
                                <>
                                    <Button type="primary" onClick={handleSubmit}>Submit</Button>
                                    <Button onClick={onCancelEdit}>Cancel</Button>
                                </>
                            ) : (
                                <Button onClick={handleStartEditing}>Edit</Button>
                            )
                        }
                        </Space>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <>
            {genUIData && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <Switch
                        size="small"
                        checked={showGenUI}
                        onChange={(checked) => setShowGenUI(checked)}
                    />
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                        {showGenUI ? 'Generative UI' : 'Raw Data'}
                    </Typography.Text>
                </div>
            )}
            {genUIData && showGenUI ? (
                <GenUIRenderer components={genUIData} />
            ) : (
                dataContent
            )}
        </>
    );
});

PackDataExpandedRow.displayName = 'PackDataExpandedRow';

export default PackDataExpandedRow;
